import { Box, Button as HeaderCustomButton, Stack } from '@mui/material';
import { Typography, Tooltip, colors } from '@sweep-io/sweep-design';
import { useLocation } from 'react-router';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons/ChevronDown';
import { DashboardSubmenu } from './DashboardSubmenu';
import { HOVER_COLOR, ACTIVE_COLOR } from './utils/colors';
import type { MenuColumn } from './utils/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { designSystemElevations } from '../../../appTheme/appTheme';
import { handleNavigationWithModifiers } from './utils/navigationUtils';
import { OrgTypeToName } from '../../pages/environments/orgTypeToName';
import { useDefaultEnv } from '../../pages/environments/DefaultEnvContext';

interface HeaderButtonProps {
  label: string;
  onClick: () => void;
  route: string;
  Icon: any;
  menuColumns?: MenuColumn[];
  isDisabled?: boolean;
}

export const HeaderButton = ({
  label,
  route,
  onClick,
  Icon,
  menuColumns,
  isDisabled,
}: HeaderButtonProps) => {
  const location = useLocation();
  const defaultEnv = useDefaultEnv();
  const orgType = defaultEnv?.orgType;
  const disabledTooltip =
    isDisabled && orgType ? `Not available in ${OrgTypeToName[orgType] ?? 'this'} environment` : '';
  const [isSubmenuVisible, setIsSubmenuVisible] = useState(false);
  const [isSubmenuClicked, setIsSubmenuClicked] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && (!event.target || !ref.current.contains(event.target as HTMLElement))) {
      setIsSubmenuClicked(false);
      setIsSubmenuVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const checkDoesChildHaveRoutes = useCallback(
    (columns: MenuColumn[]): boolean => {
      if (!columns) {
        return false;
      }
      for (const column of columns) {
        for (const item of column.menuItems) {
          if (location.pathname.includes(item.route)) {
            return true;
          }
          if (item.subMenuItems && item.subMenuId) {
            const hasMatch = checkDoesChildHaveRoutes([
              {
                columnId: item.subMenuId,
                columnLabel: item.subMenuId,
                menuItems: item.subMenuItems,
              },
            ]);
            if (hasMatch) {
              return true;
            }
          }
        }
      }
      return false;
    },
    [location.pathname],
  );

  const handleMouseEnter = useCallback(() => {
    if (menuColumns && !isDisabled) {
      setIsSubmenuVisible(true);
    }
  }, [menuColumns, isDisabled]);

  const handleMouseLeave = useCallback(() => {
    if (menuColumns && !isDisabled) {
      setTimeout(() => {
        setIsSubmenuVisible(false);
      }, 150);
    }
  }, [menuColumns, isDisabled]);

  const isChildPath = menuColumns ? checkDoesChildHaveRoutes(menuColumns) : false;
  const isActive = location.pathname.includes(route) || isChildPath;
  const buttonAndIconsColor = isDisabled ? colors.grey[500] : colors.grey[900];

  const buttonContent = (
    <Stack
      sx={{ m: '8px 0px' }}
      onMouseEnter={() => {
        if (!isSubmenuClicked) {
          handleMouseEnter();
        }
      }}
      onMouseLeave={() => {
        if (!isSubmenuClicked) {
          handleMouseLeave();
        }
      }}
      ref={ref}
    >
      <Tooltip title={disabledTooltip} placement="bottom">
        <HeaderCustomButton
          variant="contained"
          color="primary"
          sx={{
            background: isActive ? ACTIVE_COLOR : isSubmenuClicked ? HOVER_COLOR : colors.white,
            color: colors.grey[900],
            whiteSpace: 'nowrap',
            p: '8px 12px',
            borderRadius: '8px',
            cursor: isDisabled ? 'default' : 'pointer',
            '&:hover': {
              background: isDisabled ? colors.white : isActive ? ACTIVE_COLOR : HOVER_COLOR,
            },
            ...(isDisabled && {
              color: colors.grey[500],
              background: colors.white,
              cursor: 'default',
              border: 'none',
            }),
            height: '40px',
          }}
          onClick={(event) => {
            if (isDisabled) return;
            if (!menuColumns) {
              handleNavigationWithModifiers(event, route, onClick);
              event.stopPropagation();
            } else {
              setIsSubmenuClicked(true);
            }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ svg: { path: { stroke: buttonAndIconsColor } } }}
          >
            {Icon}
            <Typography variant="body" color={buttonAndIconsColor}>
              {label}
            </Typography>
          </Stack>
          {!!menuColumns && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 0.5 }}>
              <ChevronDown size="small" color={buttonAndIconsColor} />
            </Box>
          )}
          {menuColumns && (
            <Stack
              sx={{
                borderRadius: '8px',
                position: 'absolute',
                top: '44px',
                zIndex: 12,
                background: colors.white,
                boxShadow: designSystemElevations.Elevation7,
                display: isSubmenuVisible ? 'block' : 'none',
                textAlign: 'left',
                left: 0,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-5px',
                  left: 0,
                  right: 0,
                  height: '5px',
                  background: 'transparent',
                },
              }}
            >
              <DashboardSubmenu
                menuColumns={menuColumns}
                onSelect={() => {
                  setIsSubmenuClicked(false);
                }}
              />
            </Stack>
          )}
        </HeaderCustomButton>
      </Tooltip>
    </Stack>
  );

  return buttonContent;
};
